'use client';

import { ChangeEvent, FC, KeyboardEvent, Ref, useState } from 'react';

import classNames from 'classnames';

import { useControlledValue } from '../../../../hooks/useControlledValue/useControlledValue';
import { useForwardedRef } from '../../../../hooks/useForwardedRef/useForwardedRef';
import useId from '../../../../hooks/useId/useId';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';
import { InputFieldAction } from '../InputFieldAction/InputFieldAction';

interface Props {
   inputRef?: Ref<HTMLInputElement>;
   id?: string;
   name?: string;
   actionIcon: string;
   autocomplete?: 'on' | 'off';
   clearable?: boolean;
   disabled?: boolean;
   supportText?: string;
   label?: string;
   leadingIcon?: string;
   maxLength?: number;
   readOnly?: boolean;
   required?: boolean;
   suffix?: string;
   placeholder?: string;
   trailingIcon?: string;
   value?: string;
   defaultValue?: string;
   width?: string | number;
   onClear?: () => void;
   onChange?: (value: string) => void;
   onEnter?: (value: string) => void;
   onActionClick?: (value: string) => void;
   onFocus?: () => void;
   onBlur?: () => void;
   autoFocus?: boolean;
}

export const InputGroup: FC<Props> = ({
   inputRef: refFromProps,
   id: idFromProps,
   name,
   actionIcon,
   autocomplete = 'on',
   clearable = false,
   disabled = false,
   supportText,
   label,
   leadingIcon,
   maxLength = 255,
   readOnly = false,
   required = false,
   suffix,
   placeholder,
   trailingIcon,
   value: valueFromProps,
   defaultValue,
   width,
   onClear,
   onChange,
   onEnter,
   onActionClick,
   onFocus,
   onBlur,
   autoFocus
}) => {
   const id = useId(idFromProps, 'input-group');

   const [value, setValue] = useControlledValue(valueFromProps, onChange, defaultValue || '');
   const [focused, setFocused] = useState(false);

   const inputRef = useForwardedRef(refFromProps);

   /* istanbul ignore next */
   const handleFocus = () => {
      if (!focused) {
         setFocused(true);
         onFocus?.();
      }
   };

   /* istanbul ignore next */
   const handleBlur = () => {
      if (focused) {
         setFocused(false);
         onBlur?.();
      }
   };

   /* istanbul ignore next */
   const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
   };

   const handleClear = () => {
      inputRef.current?.focus();
      setValue('');
      onClear?.();
   };

   /* istanbul ignore next */
   const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
         onEnter?.(value);
         inputRef.current?.blur();
      }
   };

   /* istanbul ignore next */
   const handleActionClick = () => {
      onActionClick?.(value);
   };

   return (
      <div data-testid="input-group" style={{ width }} className="gap-component-input-input-group-container-spacing-outer-gap flex w-full flex-col">
         <div className={`group relative cursor-text ${disabled ? 'pointer-events-none' : ''}`}>
            {label && (
               <label
                  data-testid="input-group-label"
                  htmlFor={id}
                  style={{
                     maxWidth: 'calc(100% - var(--component-input-input-field-action-container-sizing-min-size) - var(--component-input-input-group-container-spacing-padding-left) - var(--component-input-input-group-container-spacing-gap))'
                  }}
                  className={classNames(
                     'absolute left-0 top-0 flex items-center transition-all cursor-pointer',
                     readOnly && 'pointer-events-none',
                     !disabled && (value !== '' || focused || placeholder) && 'text-component-input-input-group-label-color-text-neutral-enabled-as-label',
                     !disabled && (focused || placeholder) && 'text-component-input-input-group-label-color-text-neutral-focused',
                     !disabled && value === '' && !focused && 'text-component-input-input-group-label-color-text-neutral-enabled-as-placeholder',
                     disabled && 'text-component-input-input-group-label-color-text-neutral-disabled',
                     (focused || placeholder) && 'h-[var(--component-input-input-group-container-border-width-focused)]',
                     value !== '' && !focused && 'h-[var(--component-input-input-group-container-border-width-enabled)]',
                     !focused && value === '' && !placeholder && 'h-full',
                     (value !== '' || focused || placeholder) && 'bg-component-input-input-group-container-color-background px-component-input-input-group-label-spacing-padding-x',
                     value === '' && !focused && leadingIcon
                        ? 'phone:ml-[calc(var(--component-input-input-group-container-spacing-padding-left-icon)+var(--component-input-input-group-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-mobile-body))] tab-port:ml-[calc(var(--component-input-input-group-container-spacing-padding-left-icon)+var(--component-input-input-group-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] tab-land:ml-[calc(var(--component-input-input-group-container-spacing-padding-left-icon)+var(--component-input-input-group-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] ml-[calc(var(--component-input-input-group-container-spacing-padding-left-icon)+var(--component-input-input-group-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-desktop-body))]'
                        : 'ml-component-input-input-group-container-spacing-padding-left-top-label'
                  )}
               >
                  <Typography variant={value !== '' || focused || placeholder ? 'caption' : 'body'} color="inherit" maxLines="1">
                     {label}
                     {required && '*'}
                  </Typography>
               </label>
            )}

            <div
               data-testid="input-group-container"
               className={classNames(
                  focused && 'border-component-input-input-group-container-border-width-focused',
                  focused && 'border-component-input-input-group-container-color-border-neutral-focused',
                  'border-component-input-input-group-container-color-border-neutral-enabled',
                  !readOnly && 'hover:border-component-input-input-group-container-color-border-neutral-hover',
                  'min-h-component-input-input-group-container-sizing-min-height',
                  'gap-component-input-input-group-container-spacing-gap',
                  'bg-component-input-input-group-container-color-background',
                  'rounded-component-input-input-group-container-border-radius',
                  'border-component-input-input-group-container-border-width-enabled',
                  'box-border',
                  'flex',
                  'items-center',
                  'justify-between',
                  'overflow-hidden',
                  'transition-all',
                  'min-w-fit'
               )}
            >
               <div
                  className={classNames(
                     'gap-component-input-input-group-container-spacing-gap flex h-full flex-1 cursor-text justify-between pr-0',
                     leadingIcon ? 'pl-component-input-input-group-container-spacing-padding-left-icon' : 'pl-component-input-input-group-container-spacing-padding-left-no-icon',
                     readOnly && 'pointer-events-none'
                  )}
               >
                  {leadingIcon && (
                     <div
                        data-testid="input-group-leading-icon"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                           disabled ? 'text-component-input-input-group-icon-start-color-text-on-surface-disabled' : 'text-component-input-input-group-icon-start-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon={leadingIcon} />
                     </div>
                  )}
                  <div className="flex h-full flex-1 items-center">
                     <input
                        data-testid="input-group-input"
                        autoComplete={autocomplete}
                        className="text-component-input-input-group-input-color-text-on-surface-enabled disabled:text-component-input-input-group-input-color-text-on-surface-disabled typography-component-typography-body-regular-md w-full bg-transparent outline-none"
                        disabled={disabled}
                        id={id}
                        maxLength={maxLength}
                        name={name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onKeyUp={handleKeyPress}
                        readOnly={readOnly}
                        ref={inputRef}
                        required={required}
                        type="text"
                        value={value}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                     />
                  </div>
                  {suffix && (
                     <div data-testid="input-group-suffix" className={`flex items-center ${disabled ? 'text-component-input-input-group-suffix-color-text-disabled' : 'text-component-input-input-group-suffix-color-text-enabled'}`}>
                        <Typography variant="body" color="inherit">
                           {suffix}
                        </Typography>
                     </div>
                  )}
                  {clearable && value !== '' && (
                     <div
                        data-testid="input-group-clear"
                        onClick={handleClear}
                        role="presentation"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center cursor-pointer ${
                           disabled ? 'text-component-input-input-group-icon-end-color-text-on-surface-disabled' : 'text-component-input-input-group-icon-end-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon="times-circle" />
                     </div>
                  )}
                  {trailingIcon && (
                     <div
                        data-testid="input-group-trailing-icon"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                           disabled ? 'text-component-input-input-group-icon-end-color-text-on-surface-disabled' : 'text-component-input-input-group-icon-end-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon={trailingIcon} />
                     </div>
                  )}
               </div>
               <div className="pr-component-input-input-group-container-spacing-padding-right flex h-full items-center justify-center">
                  <InputFieldAction icon={actionIcon} disabled={disabled} onClick={handleActionClick} />
               </div>
            </div>
         </div>
         {supportText && (
            <div
               data-testid="input-group-support-text"
               className={`pl-component-input-input-group-supporting-text-spacing-padding-left
                        ${!disabled ? 'text-component-input-input-group-supporting-text-color-text-neutral-enabled' : ''}
                        ${disabled ? 'text-component-input-input-group-supporting-text-color-text-neutral-disabled' : ''}
                        `.replace(/\s+/g, ' ')}
            >
               {supportText && (
                  <Typography variant="caption" color="inherit">
                     {supportText}
                  </Typography>
               )}
            </div>
         )}
      </div>
   );
};
